import { type IExhibitionDateFields } from '@metmuseum/types';
import { newYorkizeDate } from 'helpers/date-time';
import { add, isThisYear, subMilliseconds } from 'date-fns';
import { toDate } from 'date-fns-tz';

class ExhibitionDates implements IExhibitionDateFields {
	endDate: string | null;
	endDateOverride: string | null;
	startDate: string;
	startDateOverride: string | null;
	newYorkDates: {
		start: Date;
		end: Date;
	};

	constructor(dates: IExhibitionDateFields) {
		this.startDate = dates.startDate;
		this.startDateOverride = dates.startDateOverride?.length
			? dates.startDateOverride
			: null;
		this.endDate = dates.endDate || null;
		this.endDateOverride = dates.endDateOverride?.length
			? dates.endDateOverride
			: null;

		this.newYorkDates = {
			start: newYorkizeDate(this.startDate), // startDate (input) "2023-06-19"| new Date(newYorkDates.start): 2023-06-29T04:00:00.000Z ✅ UTC 4am aka "startOfDay" for NYC
			end: this.endDate ? this.newYorkizeEndDate() : null,
		};
	}

	newYorkizeEndDate() {
		return add(
			subMilliseconds(
				newYorkizeDate(this.endDate), // newYorkize the accidentally UTC endDate
				1,
			), // subtract 1ms
			{ hours: 24 }, // add 24 hours to roughly get "endOfDay"
		); // endDate (input): "2023-06-30" | new Date(newYorkDates.end):  2023-07-01T03:59:59.999Z ✅ UTC 3:59am aka "endOfDay" for NYC
	}

	status(): 'upcoming' | 'current' | 'past' {
		if (this.hasEnded()) return 'past';
		if (this.isCurrent()) return 'current';
		if (this.isUpcoming()) return 'upcoming';
	}

	hasStarted(): boolean {
		return this.newYorkDates.start.getTime() <= Date.now();
	}

	neverEnds(): boolean {
		return this.newYorkDates.end === null;
	}

	hasEnded(): boolean {
		return !this.neverEnds() && this.newYorkDates.end.getTime() <= Date.now();
	}

	isUpcoming(): boolean {
		return this.newYorkDates.start.getTime() > Date.now();
	}

	isCurrent(): boolean {
		return this.hasStarted() && !this.hasEnded();
	}

	hasOverrides(): boolean {
		return !!this.startDateOverride || !!this.endDateOverride;
	}

	startsThisYear(): boolean {
		return isThisYear(toDate(this.startDate));
	}

	endsThisYear(): boolean {
		if (this.neverEnds()) return false;
		return isThisYear(toDate(this.endDate));
	}
}

export default ExhibitionDates;
